import React, { Component } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import Spinner from 'core/components/Spinner';
import { SIGN_IN, FORGOT_PASSWORD, CREATE_ACCOUNT } from './actions';
import { Wrapper } from './loginStyles';
import debounce from 'lodash.debounce';

const loadableOptions = {
	fallback: <Spinner />
};
const CreateAccount = loadable(() => import('./CreateAccount'), loadableOptions);
const ForgotPassword = loadable(() => import('./ForgotPassword'), loadableOptions);
const SignIn = loadable(() => import('./SignIn'), loadableOptions);

class LoginWorkflow extends Component {
	state = {
		current: this.props.accountCookied ? SIGN_IN : CREATE_ACCOUNT,
		email: this.props.initialEmail || '',
		signInPassword: '',
		createAccountPassword: '',
		fullName: this.props.initialFullName || '',
		wantsPromotions: false,
	}


	changeCurrent = newCurrent => (e) => {
		// Don't let click propagate to LoginBubble/Toggle 'clickedOutside' listener
		e.stopPropagation();
		const { loading, onChangeView } = this.props;
		const { current } = this.state;
		const notLoading = loading === false;
		const notCurrent = current !== newCurrent;

		if (notLoading && notCurrent) {
			this.setState({ current: newCurrent }, () => {
				onChangeView && onChangeView(newCurrent);
			});
		}
	}

	areWe = check => this.state.current === check;

	// https://facebook.github.io/react/docs/forms.html
	inputChangeHandler = event => {
		const target = event.target;
		const value = target.type === 'checkbox' ? target.checked : target.value;
		const name = target.name;

		if (name === 'fullName') {
			this.props.clearErrors('createAccount');
		}

		this.setState({
			[name]: value
		});

		if (name === 'email' && this.props.isCheckout) {
			this.onEmailEntered();
			this.props.clearErrors('signIn');
		}

		if (name === 'signInPassword') {
			this.props.clearErrors('signIn');
		}
	}

	// Once an email has been entered and the input field idle for 1000ms, send the email
	// to braze (via our own backend for deduping).
	// First do a very basic check to make sure it looks like a valid email.
	onEmailEntered = debounce(() => {
		let atPos = this.state.email.indexOf('@');
		let dtPos = this.state.email.indexOf('.', atPos);

		if (atPos === -1 || dtPos === -1 || this.state.email.length < (dtPos + 2)) {
			return;
		}

		if (window.BrazeUtil)
			window.BrazeUtil.addToCartGuest(this.state.email);
	}, 1000)

	fillSuggestedEmail = suggestion => this.setState({ email: suggestion });

	// A way to let a higher component deal with the logic
	// featuring some currying to keep the event going higher up
	// https://hackernoon.com/javascript-and-functional-programming-currying-pt-4-96e3230782ab
	formHandler = (propKey, ...stateProps) => event => {
		event.preventDefault();
		const callbackFromProps = this.props[propKey];
		return callbackFromProps(...stateProps);
	}

	componentDidUpdate (prevProps) {
		const { accountCookied: cookie, loginView } = this.props;
		const { accountCookied: preCookie } = prevProps;

		if (!loginView && cookie !== preCookie) {
			this.setState({ current: SIGN_IN });
			return;
		}
	}

	componentDidMount () {
		this.shouldFireLoginIntent();
		const { loginView } = this.props;
		if (!loginView) return;
		let current = SIGN_IN;
		switch (loginView) {
			case 'login':
				current = SIGN_IN;
				break;
			case 'forgot':
				current = FORGOT_PASSWORD;
				break;
			case 'register':
				current = CREATE_ACCOUNT;
				break;
		}
		this.setState({ current });
	}

	shouldFireLoginIntent = () => {
		if (!this.props.intentOnMount) return;

		const evt = new CustomEvent('loginIntent');
		window.dispatchEvent(evt);
	}

	render () {
		const {
			changeCurrent,
			areWe,
			inputChangeHandler,
			formHandler,
			fillSuggestedEmail,
		} = this;

		const {
			email,
			signInPassword,
			fullName,
			createAccountPassword,
			wantsPromotions,
		} = this.state;

		const sharedProps = {
			changeCurrent,
			...this.state,
			inputChangeHandler,
			loading: this.props.loading,
			setLoading: this.props.setLoading,
			forgotSuccess: this.props.forgotSuccess,
			captchaReady: this.props.captchaReady,
			modalMode: this.props.modalMode,
			isCheckout: this.props.isCheckout,
		};

		const {
			outsideUsa,
			signInError,
			forgotError,
			facebookClick,
			googleClick,
			createAccountError,
			createAccountErrorCode,
			createAccountMsg,
			onGuestCheckoutSelected,
		} = this.props;

		const signInHandler = formHandler('signInHandler', email, signInPassword);
		const forgotHandler = formHandler('forgotHandler', email);
		const createAccountHandler = formHandler('createAccountHandler', email, createAccountPassword, fullName, wantsPromotions);
		const hasEmailError = createAccountErrorCode === 'invalid_email';
		const hasFullNameError = createAccountErrorCode === 'invalid_full_name';

		return (
			<Wrapper>
				{areWe(SIGN_IN) ? (
					<SignIn
						signInHandler={signInHandler}
						signInError={signInError}
						facebookClick={facebookClick}
						googleClick={googleClick}
						fillSuggestedEmail={fillSuggestedEmail}
						onGuestCheckoutSelected={onGuestCheckoutSelected}
						{ ...sharedProps }
					/>
				) : null}

				{areWe(FORGOT_PASSWORD) ? (
					<ForgotPassword
						forgotHandler={forgotHandler}
						forgotError={forgotError}
						{ ...sharedProps }
					/>
				) : null}

				{areWe(CREATE_ACCOUNT) ? (
					<CreateAccount
						outsideUsa={outsideUsa}
						createAccountHandler={createAccountHandler}
						facebookClick={facebookClick}
						googleClick={googleClick}
						createAccountError={createAccountError}
						hasEmailError={hasEmailError}
						hasFullNameError={hasFullNameError}
						createAccountMsg={createAccountMsg}
						fillSuggestedEmail={fillSuggestedEmail}
						onGuestCheckoutSelected={onGuestCheckoutSelected}
						{ ...sharedProps }
					/>
				) : null}
			</Wrapper>
		);
	}
}

LoginWorkflow.propTypes = {
	outsideUsa: PropTypes.bool,
	signInHandler: PropTypes.func,
	forgotHandler: PropTypes.func,
	createAccountHandler: PropTypes.func,
	signInError: PropTypes.bool,
	forgotError: PropTypes.bool,
	facebookClick: PropTypes.func,
	googleClick: PropTypes.func,
	loading: PropTypes.bool.isRequired,
	setLoading: PropTypes.func,
	forgotSuccess: PropTypes.bool.isRequired,
	captchaReady: PropTypes.bool,
	createAccountError: PropTypes.bool,
	createAccountErrorCode: PropTypes.string,
	createAccountMsg: PropTypes.string,
	modalMode: PropTypes.bool,
	isCheckout: PropTypes.bool,
	accountCookied: PropTypes.bool,
	loginView: PropTypes.oneOf(['login', 'register', 'forgot']),
	intentOnMount: PropTypes.bool,
	initialEmail: PropTypes.string,
	initialFullName: PropTypes.string,
	onChangeView: PropTypes.func,
	onGuestCheckoutSelected: PropTypes.func,
	clearErrors: PropTypes.func,
};

LoginWorkflow.defaultProps = {
	// When false, will not fire `loginIntent` on component mount
	// instead, when ready trigger manually (see `LoginBubble` for example)
	intentOnMount: true,
};

export default LoginWorkflow;
